import { Button, Icon, Input } from "@elaniintc/goes-design-system";
import {
  Background,
  BackgroundVariant,
  ConnectionLineType,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import ExecutionNode from "./components/node/execution";
import { useWorkflowStore } from "./store/workflowStore";
import { ExecutionEdge } from "./components/edge/execution";
import ExecutionSelection from "./components/execution";
import { useUIStore } from "./store/uiStore";
import ExecutionDetailsModal from "./modals/execution-details";
import NodeSearch from "./modals/node-search";

const nodeTypes = {
  "worflow-node": ExecutionNode,
};

const edgeTypes = {
  "workflow-edge": ExecutionEdge,
};

const InnerExecutionCanvas = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { zoomIn, zoomOut, getZoom, zoomTo, fitView } = useReactFlow();

  const { nodes, edges } = useWorkflowStore();
  const { executionDetailsOpen, modalState } = useUIStore();

  const onInit = useCallback(() => {
    fitView({
      maxZoom: 1,
    });
  }, [fitView]);
  const [zoom, setZoom] = useState(getZoom());
  const handleZoomIn = useCallback(() => {
    zoomIn();
    setTimeout(() => {
      setZoom(getZoom() * 100); // Update zoom state after zooming in
    }, 0);
  }, [zoomIn, getZoom]);

  const handleZoomOut = useCallback(() => {
    zoomOut();
    setTimeout(() => {
      setZoom(getZoom() * 100); // Update zoom state after zooming out
    }, 0);
  }, [zoomOut, getZoom]);

  const handleResetZoom = useCallback(() => {
    fitView();
    setZoom(200); // Reset zoom state
  }, [setZoom, fitView]);

  const [debouncedZoom] = useDebounceValue(zoom, 300);

  useEffect(() => {
    if (debouncedZoom) {
      zoomTo(debouncedZoom / 100);
    }
  }, [debouncedZoom, zoomTo]);
  return (
    <div
      className="bg-neutral-object -m-3 tablet:-m-4 laptop:-m-6 desktop:-m-8 w-[calc(100%+24px)] tablet:w-[calc(100%+32px)] laptop:w-[calc(100%+48px)] desktop:w-[calc(100%+64px)] h-[calc(100%+24px)] tablet:h-[calc(100%+32px)] laptop:h-[calc(100%+48px)] desktop:h-[calc(100%+64px)]"
      ref={reactFlowWrapper}
    >
      {executionDetailsOpen && <ExecutionDetailsModal />}
      {modalState === "search-nodes" && <NodeSearch />}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        zoomOnScroll={false}
        zoomOnPinch={false}
        snapToGrid={true}
        snapGrid={[22, 22]}
        onInit={onInit}
        multiSelectionKeyCode="Shift"
        selectionOnDrag={true}
        noDragClassName="no-drag"
        connectionLineStyle={{
          stroke: "var(--border-neutral)",
          strokeWidth: 2,
        }}
        onViewportChange={({ zoom }) => {
          setZoom(zoom * 100);
        }}
        connectionLineType={ConnectionLineType.SmoothStep}
        attributionPosition="top-right"
        proOptions={{
          hideAttribution: true,
        }}
      >
        <ExecutionSelection />
        <Background
          id={"background-1"}
          variant={BackgroundVariant.Dots}
          gap={[22, 22]}
          size={3}
          className=""
          color="var(--neutral-object-accent)"
        />
        <div className="absolute bottom-3 left-8 flex items-center gap-2 z-20">
          <div className="bg-neutral-object flex items-center rounded-lg">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              group="left"
              aria-label="zoom out"
              onClick={handleZoomOut}
            >
              <Icon type="far" name="search-minus" />
            </Button>
            <div className="relative">
              <Input
                value={zoom.toFixed(0)}
                onChange={(e) => {
                  // admit just digits
                  const value = e.target.value.replace(/\D/g, "");
                  setZoom(Number(value) || 0);
                }}
                group="middle-up"
                maxLength={3}
                variant="xs"
                className="w-16 font-geist-mono pl-4 text-body-quiet"
              />
              <span className="absolute right-0 top-1.5 flex items-center pr-2 text-sm text-body-quiet">
                %
              </span>
            </div>
            <Button
              type="button"
              variant="quiet"
              size="icon"
              aria-label="zoom in"
              group="right"
              onClick={handleZoomIn}
            >
              <Icon type="far" name="search-plus" />
            </Button>
          </div>
          <div className="bg-neutral-object flex items-center rounded-lg">
            <Button
              size="icon"
              variant="quiet"
              onClick={handleResetZoom}
              aria-label="reset zoom"
            >
              <Icon type="far" name="expand" />
            </Button>
          </div>
        </div>
        {/* <div className="absolute bottom-3 right-8 flex items-center z-20">
          <Button
            type="button"
            variant="quiet"
            size="icon"
            aria-label="undo last action"
            group="left"
            onClick={undo}
            disabled={!canUndo()}
          >
            <Icon type="far" name="undo" />
          </Button>
          <Button
            type="button"
            variant="quiet"
            size="icon"
            aria-label="redo last action"
            group="right"
            onClick={redo}
            disabled={!canRedo()}
          >
            <Icon type="far" name="redo" />
          </Button>
        </div> */}
      </ReactFlow>
    </div>
  );
};

export function ExecutionCanvas() {
  return (
    <ReactFlowProvider>
      <InnerExecutionCanvas />
    </ReactFlowProvider>
  );
}
